import React from 'react';
import { useParams } from 'react-router-dom';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import { useDataEntry } from '../../queries/dataEntryQueries';
import {
  SblcSection,
  LDDocSection,
  FormA1Section,
  StatusWaiverSection,
  ForwardContractSection,
  FormA2Section,
} from '../../components/datamanagement/bankdetails';


/**
 * BankDetailsPage — Bank Details document page for a data entry record.
 *
 * Route:       /business/bank-details/:id
 * Timeline:    Step 6 (BANK DETAILS) active
 *
 * This page is intentionally thin — it handles:
 *   1. Route param extraction (id)
 *   2. Data fetching (useDataEntry)
 *   3. Loading / error states
 *   4. Layout (DeTimeline + col-lg-9 sections + col-lg-3 DeRightSidebar)
 *
 * All section content lives in:
 *   src/components/datamanagement/bankdetails/
 *     SblcSection.jsx   → Section 1 : SBLC Letter + Download
 *     BankSection2.jsx  → Section 2 : (Status Waiver / LDDoc — TBD)
 *     BankSection3.jsx  → Section 3 : (Forward Contract — TBD)
 */
const BankDetailsPage = () => {
  const { id } = useParams();
  const { data: retrievedData, isLoading, isError } = useDataEntry(id);

  return (
    <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
      <div
        className="d-flex flex-column flex-grow-1 mx-auto w-100"
        style={{ maxWidth: '1280px' }}
      >
        {/* ── Step Progress Timeline — Step 6 active (BANK DETAILS) ── */}
        <div className="flex-shrink-0 pb-1">
          <DeTimeline activeStep={6} />
        </div>

        {/* ── Bootstrap 5 two-column layout ── */}
        <div className="row g-3 flex-grow-1 overflow-hidden">

          {/* ── Left Column: Section Components ── */}
          <div className="col-12 col-lg-9 d-flex flex-column">
            {isLoading ? (
              <div className="card shadow-sm w-100 p-5 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading Bank Details...
              </div>
            ) : isError ? (
              <div className="card shadow-sm w-100 p-5 text-center text-danger">
                Failed to load data. Please try again.
              </div>
            ) : (
              <div
                className="w-100 d-flex flex-column gap-4 align-items-center"
                style={{ minHeight: 'calc(100vh - 180px)' }}
              >
                {/* Section 1 — SBLC Letter */}
                <SblcSection retrievedData={retrievedData} />

                {/* Section 2 — LDDoc (Memorandum of Charge/Lien) */}
                <LDDocSection retrievedData={retrievedData} />

                {/* Section 3 — Form A1 */}
                <FormA1Section retrievedData={retrievedData} />

                {/* Section 4 — Status Waiver */}
                <StatusWaiverSection retrievedData={retrievedData} />

                {/* Section 5 — Forward Contract (4 sheets) */}
                <ForwardContractSection retrievedData={retrievedData} />

                {/* Section 6 — Form A2 (5 sheets) */}
                <FormA2Section retrievedData={retrievedData} />
              </div>

            )}
          </div>

          {/* ── Right Sidebar Column ── */}
          <div className="col-12 col-lg-3">
            <DeRightSidebar id={id} activeStep={6} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default BankDetailsPage;
